/**
 * Subscreen SettingsInstagramConnect
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react'
import {
  Box,
  Typography,
  IconButton,
  Button,
  Grid,
  Dialog,
  DialogContent,
  Theme,
} from '@mui/material'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import CloseIcon from '@mui/icons-material/Close'
import {
  getConnectedInstagramPage,
  getConnectionChanged,
  getInstagramConnectHash,
  getInstagramPages,
  getTokenChanged,
  getUser,
  getUserPages,
  namedRequestData,
} from '../../../store/common/common.selectors'
import {
  GetInstagramConnectDataRequest,
  InstagramConnectDataElement,
} from '@3m5/sm_publish-frontend-api'
import {
  getInstagramConnectDataInitiated,
  getSmPublishAuthTokenInitiated,
  initiateInstagramConnectInitiated,
  performConnectInitiated,
  performDisconnectInitiated,
  setConnectedFacebookPage,
  setConnectedInstagramPage,
  setInstagramConnectHash,
  setTokenChanged,
  socialmediaEditorConnectInitiated,
  socialmediaEditorDisconnectInitiated,
  socialmediaUserConnectInitiated,
  socialmediaUserDisconnectInitiated,
} from '../../../store/common/common.actions'
import {
  InitiateConnectOperationRequest,
  PerformConnectOperationRequest,
  PerformDisconnectRequest,
} from '@professocialApi/professocial-core-frontend-api/apis/SmPublishConnectApi'
import {
  EUserRole,
  NetworkType,
  SocialmediaConnectionState,
} from '@professocialApi/professocial-core-frontend-api/models'
import ConnectPages from '../components/ConnectPages'
import NoDataComponent from '@professocial/professocialFormComponents/FormComponents/NoDataComponent'
import { ISettingsConnectProps } from './SettingsConnect'
import {
  SocialmediaUserConnectRequest,
  SocialmediaUserDisconnectRequest,
} from '@professocialApi/professocial-core-frontend-api/apis/SocialmediaUserApi'
import {
  ListPageOwnersForPageRequest,
  SetUserAsPageMasterRequest,
} from '@professocialApi/professocial-core-frontend-api/apis/UserHasPageApi'
import {
  getPageMasterInitiated,
  listPageOwnersForPageInitiated,
  setIGPage,
  setUserAsPageMasterInitiated,
} from '../../../store/dashboard/dashboard.actions'
import {
  GetPageMasterRequest,
  UserSettings,
} from '@professocialApi/professocial-core-frontend-api'
import { getPageOwnersData } from '../../../store/dashboard/dashboard.selector'
import { InstagramPage } from '@3m5/sm_publish-frontend-api'
import PreloaderButton from '@professocial/professocialFormComponents/FormComponents/PreloaderButton'
import { ActionCall } from '@professocial/professocialTypes/types/commonTypes'
import { CoreRootState } from '../../../store/coreRoot.reducer'
import {
  SocialmediaEditorConnectRequest,
  SocialmediaEditorDisconnectRequest,
} from '@professocialApi/professocial-core-frontend-api/apis/SocialmediaEditorApi'
import { MobileView, TabletView } from 'react-device-detect'

// Icons
import InfoIcon from '@mui/icons-material/Info'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import getAppTheme from '../../../defaults/getAppTheme'
import ImageComponent from '@professocial/professocialFormComponents/FormComponents/ImageComponent'
import { makeStyles } from '@mui/styles'

const useStyles = makeStyles((theme: Theme) => {
  return {
    dialog: {
      backgroundColor: theme.palette.primary.main,
      color: 'white',
    },
    iconButton: {
      color: theme.palette.primary.main,
    },
    closeButton: {
      position: 'absolute',
      right: 8,
      top: 8,
      color: 'white',
    },
  }
})

const SettingsInstagramConnect: React.FC<ISettingsConnectProps> = (props) => {
  const { t } = useTranslation()
  const dispatch = useDispatch()

  const user: UserSettings | undefined = useSelector(getUser)
  const connectionChanged: boolean = useSelector(getConnectionChanged)
  const tokenChanged: boolean = useSelector(getTokenChanged)
  const connectedUserPages = useSelector(getUserPages)

  const [facebookConnection, setFacebookConnection] = useState<boolean>(false)
  const [disableConnection, setDisableConnection] = useState<boolean>(false)
  const [connectedInstagramPages, setConnectedInstagramPages] = useState<InstagramConnectDataElement[]>()
  const [availableInstagramPages, setAvailableInstagramPages] = useState<InstagramConnectDataElement[]>()

  const instagramConnectHash: string | undefined = useSelector(getInstagramConnectHash)
  const instagramPages: InstagramConnectDataElement[] = useSelector(getInstagramPages) || []
  const connectedPage: string | undefined = useSelector(getConnectedInstagramPage)
  const pageOwnersList = useSelector(getPageOwnersData)
  const [copiedMessageVisible, setCopiedMessageVisible] = useState(false)
  const [copiedMessageVisibleDkv, setCopiedMessageVisibleDkv] = useState(false)

  const onInitiateConnect: ActionCall | undefined = useSelector(
    (state: CoreRootState) => namedRequestData(state, initiateInstagramConnectInitiated.type)
  )

  const [openDialog, setOpenDialog] = useState(false)

  const classes = useStyles()
  const handleOpen = () => setOpenDialog(true)
  const handleClose = () => setOpenDialog(false)

  const dispatchSocialmediaUserConnect = useCallback(
    (params: SocialmediaUserConnectRequest) => dispatch(socialmediaUserConnectInitiated(params)),
    [dispatch]
  )
  const dispatchSocialmediaUserDisconnect = useCallback(
    (params: SocialmediaUserDisconnectRequest) => dispatch(socialmediaUserDisconnectInitiated(params)),
    [dispatch]
  )
  const dispatchSocialmediaEditorConnect = useCallback(
    (params: SocialmediaEditorConnectRequest) => dispatch(socialmediaEditorConnectInitiated(params)),
    [dispatch]
  )
  const dispatchSocialmediaEditorDisconnect = useCallback(
    (params: SocialmediaEditorDisconnectRequest) => dispatch(socialmediaEditorDisconnectInitiated(params)),
    [dispatch]
  )

  const dispatchSetInstagramConnectHash = useCallback(
    (params: string | undefined) => dispatch(setInstagramConnectHash(params)),
    [dispatch]
  )
  const dispatchSetConnectedInstagramPage = useCallback(
    (params: string) => dispatch(setConnectedInstagramPage(params)),
    [dispatch]
  )
  const dispatchSetConnectedFacebookPage = useCallback(
    (params: string) => dispatch(setConnectedFacebookPage(params)),
    [dispatch]
  )
  const dispatchGetToken = useCallback(() => dispatch(getSmPublishAuthTokenInitiated()), [dispatch])

  const dispatchInitiateConnect = useCallback(
    (params: InitiateConnectOperationRequest) => dispatch(initiateInstagramConnectInitiated(params)),
    [dispatch]
  )
  const dispatchPerformConnect = useCallback(
    (params: PerformConnectOperationRequest) => dispatch(performConnectInitiated(params)),
    [dispatch]
  )
  const dispatchPerformDisconnect = useCallback(
    (params: PerformDisconnectRequest) => dispatch(performDisconnectInitiated(params)),
    [dispatch]
  )
  const dispatchGetInstagramConnectData = useCallback(
    (params: GetInstagramConnectDataRequest) => dispatch(getInstagramConnectDataInitiated(params)),
    [dispatch]
  )

  const dispatchListPageOwnersForPage = useCallback(
    (params: ListPageOwnersForPageRequest) => dispatch(listPageOwnersForPageInitiated(params)),
    [dispatch]
  )
  const dispatchGetPageMaster = useCallback(
    (params: GetPageMasterRequest) => dispatch(getPageMasterInitiated(params)),
    [dispatch]
  )
  const dispatchSetPageMaster = useCallback(
    (params: SetUserAsPageMasterRequest) => dispatch(setUserAsPageMasterInitiated(params)),
    [dispatch]
  )
  const dispatchSetInstagramPage = useCallback(
    (params: InstagramPage | undefined) => dispatch(setIGPage(params)),
    [dispatch]
  )
  const dispatchSetTokenChanged = useCallback(
    (params: boolean) => dispatch(setTokenChanged(params)),
    [dispatch]
  )

  // Memoize getOwnerData to prevent infinite requests
  const getOwnerData = useCallback(
    (networkPageId: string) => {
      dispatchListPageOwnersForPage({
        networkPageId,
        networkType: NetworkType.Instagram,
      })
      dispatchGetPageMaster({
        networkPageId,
        networkType: NetworkType.Instagram,
      })
    },
    [dispatchListPageOwnersForPage, dispatchGetPageMaster]
  )

  // Optionally memoize setPageOwner as well
  const setPageOwner = useCallback(
    (data: SetUserAsPageMasterRequest) => {
      dispatchSetPageMaster(data)
    },
    [dispatchSetPageMaster]
  )

  const handleCopyDefault = () => {
    navigator.clipboard.writeText(personalizedLink).then(() => {
      setCopiedMessageVisible(true)
      setTimeout(() => {
        setCopiedMessageVisible(false)
      }, 2000)
    })
  }

  const handleCopyDkv = () => {
    navigator.clipboard.writeText(personalizedLinkDkv).then(() => {
      setCopiedMessageVisibleDkv(true)
      setTimeout(() => {
        setCopiedMessageVisibleDkv(false)
      }, 2000)
    })
  }

  const getInstagramConnectDataElements = () => {
    if (instagramConnectHash) {
      const params: GetInstagramConnectDataRequest = {
        connectHash: instagramConnectHash,
      }
      dispatchGetInstagramConnectData(params)
    }
  }

  useEffect(() => {
    if (!!instagramPages && instagramPages.length && connectedUserPages) {
      const filteredConnectedInstagramPages = instagramPages.filter(
        (inp) =>
          inp.isConnected &&
          !!connectedUserPages.find((cup) => cup.networkPageId === inp.page.networkPageId)
      )

      if (filteredConnectedInstagramPages) {
        const availablePages = instagramPages.filter(
          (igp) =>
            !filteredConnectedInstagramPages.find(
              (cigp) => cigp.page.networkPageId === igp.page.networkPageId
            )
        )
        setConnectedInstagramPages(filteredConnectedInstagramPages)
        setAvailableInstagramPages(availablePages)
      }
    }
  }, [instagramPages, connectedUserPages])

  useEffect(() => {
    if (props.connection?.state === SocialmediaConnectionState.Connected) {
      setFacebookConnection(true)
      setDisableConnection(false)

      const params: InitiateConnectOperationRequest = {
        initiateConnectRequest: {
          networkType: NetworkType.Instagram,
        },
      }
      dispatchInitiateConnect(params)
      return
    }

    if (props.connection?.state === SocialmediaConnectionState.Invalid) {
      setDisableConnection(false)
      setFacebookConnection(true)
      return
    }

    if (props.connection?.state === SocialmediaConnectionState.Disabled) {
      setDisableConnection(true)
      setFacebookConnection(false)
    }
  }, [props.connection, dispatchInitiateConnect])

  useEffect(() => {
    const page =
      connectedUserPages &&
      connectedUserPages.find((elem) => elem.networkType === 'Instagram')
    if (page) {
      dispatchSetConnectedInstagramPage(page.networkPageId)
    }
  }, [connectedUserPages, dispatchSetConnectedInstagramPage])

  useEffect(() => {
    dispatchSetInstagramConnectHash(instagramConnectHash || undefined)
  }, [tokenChanged, dispatchSetInstagramConnectHash, instagramConnectHash])

  useEffect(() => {
    dispatchGetToken()
    getInstagramConnectDataElements()
  }, [instagramConnectHash, dispatchGetToken])

  useEffect(() => {
    connectionChanged && getInstagramConnectDataElements()
  }, [connectionChanged])

  const personalizedLink = useMemo(() => {
    // Final fallback
    return 'https://vorname-nachname.ergo.de/de/Agentur/Impressum'
  }, [user])

  const personalizedLinkDkv = useMemo(() => {
    // Final fallback
    return 'https://vorname-nachname-dkv.ergo.de/de/Agentur/Impressum'
  }, [user])

  return (
    <>
      <Typography sx={{ mb: 3 }}>
        {facebookConnection
          ? props.connection?.state === SocialmediaConnectionState.Invalid
            ? t('coreSettings:igInvalidConnectionDesc')
            : t('coreSettings:instagramConnectionDesc')
          : t('coreSettings:noConnection')}
      </Typography>

      <PreloaderButton
        position="left"
        loading={onInitiateConnect?.inProgress}
        disableButton={disableConnection}
        onClick={() => {
          if (facebookConnection) {
            // Disconnect
            if (props.connection?.id && user) {
              dispatchSetConnectedInstagramPage('')
              dispatchSetConnectedFacebookPage('')
              switch (user.role) {
              case EUserRole.User: {
                const userParams: SocialmediaUserDisconnectRequest = {
                  connectionId: props.connection?.id,
                }
                dispatchSocialmediaUserDisconnect(userParams)
                setFacebookConnection(false)
                return
              }
              case EUserRole.Editor: {
                const editorParams: SocialmediaEditorDisconnectRequest = {
                  connectionId: props.connection?.id,
                }
                dispatchSocialmediaEditorDisconnect(editorParams)
                setFacebookConnection(false)
              }
              }
            }
          } else {
            // Connect
            if (props.connection?.id && user) {
              dispatchSetConnectedInstagramPage('')
              dispatchSetConnectedFacebookPage('')
              switch (user.role) {
              case EUserRole.User: {
                const userParams: SocialmediaUserConnectRequest = {
                  connectionId: props.connection?.id,
                  redirectUrl: window.location.href,
                }
                dispatchSocialmediaUserConnect(userParams)
                return
              }
              case EUserRole.Editor: {
                const editorParams: SocialmediaEditorConnectRequest = {
                  connectionId: props.connection?.id,
                  redirectUrl: window.location.href,
                }
                dispatchSocialmediaEditorConnect(editorParams)
              }
              }
            }
          }
        }}
        text={
          <Box sx={{ display: 'flex', alignItems: 'center', textTransform: 'initial' }}>
            <Typography sx={{ pr: 0.5 }}>
              {t('coreSettings:disconnectStart')}
            </Typography>
            <Typography>
              <b>profes</b>
              <i>social</i>
            </Typography>
            <Box sx={{ display: 'flex', fontSize: '0.6rem', alignSelf: 'start', pl: 0.2 }}>
              ©
            </Box>
            <Typography sx={{ pl: 0.5 }}>
              {facebookConnection ? t('coreSettings:disconnectEnd') : t('coreSettings:connectEnd')}
            </Typography>
          </Box>
        }
      />

      {facebookConnection && instagramConnectHash && (
        <>
          {/* CONNECTED PAGES + ADDITIONAL INFO SECTION USING A TWO-COLUMN LAYOUT */}
          <Box sx={{ mt: 4 }}>
            <Grid container spacing={2}>
              {/* LEFT COLUMN: CONNECTED PAGES */}
              <Grid item xs={12} md={5}>
                <Typography variant="h4" sx={{ mb: 2 }}>
                  {t('coreSettings:instagramConnectedPages')}
                </Typography>
                {!!connectedInstagramPages && !!connectedInstagramPages.length ? (
                  <>
                    {connectedInstagramPages.map((instagramPage: InstagramConnectDataElement, index: number) => {
                      if (instagramPage.isConnected) {
                        return (
                          <ConnectPages
                            key={index}
                            disabled={false}
                            canAddPage={true}
                            handleGetOwnerData={getOwnerData}
                            handleSetPageOwner={setPageOwner}
                            pageOwnersList={undefined}
                            page={instagramPage}
                            networkType={NetworkType.Instagram}
                            disconnect={(networkPageId: string) => {
                              const params: PerformDisconnectRequest = {
                                performConnectRequest: {
                                  connectHash: instagramConnectHash,
                                  networkPageId: networkPageId,
                                },
                              }
                              dispatchPerformDisconnect(params)
                              dispatchSetConnectedInstagramPage('')
                              dispatchSetInstagramPage(undefined)
                            }}
                          />
                        )
                      }
                      return null
                    })}
                  </>
                ) : (
                  <NoDataComponent
                    noDataText={t('coreSettings:noConnectedPages')}
                    alignment="left"
                    margin="16px 0"
                  />
                )}
              </Grid>

              {/* RIGHT COLUMN: Additional Info (Hinweis) */}
              <Grid item xs={12} md={7}>
                <Typography variant="body1" paragraph>
                  <b>Hinweis:</b> Beachten Sie Ihre Impressumspflicht auf Instagram!
                  <IconButton className={classes.iconButton} onClick={handleOpen} size="small">
                    <InfoIcon />
                  </IconButton>
                  <Dialog open={openDialog} onClose={handleClose} maxWidth="xs" fullWidth>
                    <IconButton aria-label="close" className={classes.closeButton} onClick={handleClose}>
                      <CloseIcon />
                    </IconButton>
                    <DialogContent dividers className={classes.dialog}>
                      <Typography variant="body1" paragraph>
                        <br />
                        Kopieren Sie den Impressums-Link, personalisieren Sie ihn und pflegen Sie ihn an dieser Stelle innerhalb der mobilen Instagram-App ein
                      </Typography>
                      <ImageComponent
                        width={300}
                        src="/src/shared/assets/images/profil_bearbeiten_instagram.png"
                        fallback="/src/shared/assets/images/profil_bearbeiten_instagram.png"
                        style={{ margin: '0 auto', display: 'block' }}
                      />
                    </DialogContent>
                  </Dialog>
                  <br />
                  <br />
                  1. Personalisieren Sie Ihren Impressums-Link.
                  <br />
                  ERGO: https://<b>vorname-nachname</b>.ergo.de/de/Agentur/Impressum
                  <Box sx={{ position: 'relative', display: 'inline-block' }}>
                    <IconButton className={classes.iconButton} onClick={handleCopyDefault}>
                      <ContentCopyIcon />
                    </IconButton>
                    {copiedMessageVisible && (
                      <Box
                        sx={{
                          position: 'absolute',
                          top: 'calc(100% + 4px)',
                          left: '50%',
                          transform: 'translateX(-50%)',
                          backgroundColor: 'rgba(0, 0, 0, 0.8)',
                          color: '#fff',
                          padding: '4px 8px',
                          borderRadius: '4px',
                          fontSize: '0.75rem',
                          zIndex: 9999,
                        }}
                      >
                        Impressums-Link kopiert
                      </Box>
                    )}
                  </Box>
                  <br />
                  DKV: https://<b>vorname-nachname</b>-dkv.ergo.de/de/Agentur/Impressum
                  <Box sx={{ position: 'relative', display: 'inline-block' }}>
                    <IconButton className={classes.iconButton} onClick={handleCopyDkv}>
                      <ContentCopyIcon />
                    </IconButton>
                    {copiedMessageVisibleDkv && (
                      <Box
                        sx={{
                          position: 'absolute',
                          top: 'calc(100% + 4px)',
                          left: '50%',
                          transform: 'translateX(-50%)',
                          backgroundColor: 'rgba(0, 0, 0, 0.8)',
                          color: '#fff',
                          padding: '4px 8px',
                          borderRadius: '4px',
                          fontSize: '0.75rem',
                          zIndex: 9999,
                        }}
                      >
                        Impressums-Link kopiert
                      </Box>
                    )}
                  </Box>
                  <br />
                  2. Öffnen Sie die mobile Instagram-App.
                  <br />
                  3. Gehen Sie auf Ihr Profil, klicken Sie auf &quot;Bearbeiten&quot;.
                  <br />
                  4. Fügen Sie Ihren personalisierten Impressums-Link in das Eingabefeld &quot;Link&quot; ein.
                </Typography>
                <MobileView>
                  <Button
                    variant="contained"
                    color="primary"
                    href="https://www.instagram.com/accounts/edit/"
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{ mb: 2 }}
                  >
                    {t('coreSettings:externalLinkLabel')}
                  </Button>
                </MobileView>
              </Grid>
            </Grid>
          </Box>

          {/* AVAILABLE PAGES SECTION */}
          <Box sx={{ mt: 4 }}>
            <Grid container spacing={2}>
              {/* LEFT COLUMN: Available Pages */}
              <Grid item xs={12} md={6}>
                <Typography variant="h4" sx={{ mb: 2 }}>
                  {t('coreSettings:instagramAvailablePages')}
                </Typography>
                {!!availableInstagramPages && !!availableInstagramPages.length ? (
                  <>
                    {availableInstagramPages.map((instagramPage: InstagramConnectDataElement, index: number) => (
                      <ConnectPages
                        key={index}
                        disabled={!!connectedPage && !!user?.canAddInstagramPage}
                        canAddPage={user?.canAddInstagramPage}
                        page={instagramPage}
                        networkType={NetworkType.Instagram}
                        connect={(networkPageId: string) => {
                          const params: PerformConnectOperationRequest = {
                            performConnectRequest: {
                              connectHash: instagramConnectHash,
                              networkPageId: networkPageId,
                            },
                          }
                          dispatchSetTokenChanged(false)
                          dispatchPerformConnect(params)
                          dispatchSetConnectedInstagramPage(networkPageId)
                        }}
                      />
                    ))}
                  </>
                ) : (
                  <NoDataComponent
                    noDataText={t('coreSettings:noConnectedPages')}
                    alignment="left"
                    margin="16px 0"
                  />
                )}
              </Grid>

              {/* RIGHT COLUMN (optional) */}
              <Grid item xs={12} md={6}>
                {/* Additional content can go here */}
              </Grid>
            </Grid>
          </Box>
        </>
      )}
    </>
  )
}

export default SettingsInstagramConnect
